import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { SButton, SIcon, SPopover, STooltip } from '@chargebee/sting-react';
import {
  BULK_EXPORT_INELIGIBLE_SELECTION_TOOLTIP,
  bulkExportRequiresStarterPremium,
  exportBulkPlaysAsZip,
  isBulkExportSelectionEligible,
} from '@bbkAdminComponents/play/play-bulk-export';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';
import { TriggerEventType } from '@bbkAdminComponents/play/play-types';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import {
  selectAppId,
  selectCompanyInternalName,
} from '@bbkAdminRedux/app/selectors';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import {
  selectExpPerfReportNav,
  selectOfferPerfReportNav,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import { useGetBrandOptions } from '@bbkAdminRedux/rtkq/brands.slice';
import { companyAppSettingsSlice } from '@bbkAdminRedux/rtkq/company-app-settings.slice';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { type Play } from '@bbkAdminRedux/rtkq/play.slice';
import {
  ReportType,
  useExportCustomersData,
  type ReportFilter,
  type ReportFilters,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { type LCOReportOpts } from '@bbkAdminUtils/api-client/reports-api';
import { cs } from '@bbkAdminUtils/css-utils';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { Icons } from '@bbkAdminUtils/icons';
import { useGetProductLicense, usePlans } from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';

const getTargetType = (play: Play): 'HOSTED_PAGE' | 'OFFER' =>
  play.trigger_rules?.event_type === TriggerEventType.ON_CLICK
    ? 'HOSTED_PAGE'
    : 'OFFER';

const usePlayExport = (
  buildFilters: () => ReportFilters,
  opts?: { orFilters?: boolean }
) => {
  const appKey = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);
  const showNewPlayReportsUI = useGetProductLicense().agsLicense;
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const exportLCOReportingData = useExportCustomersData();

  const [openExportMenu, setOpenExportMenu] = useState(false);

  const exportMenuAnchorRef = useRef<any>(null);

  const exportData = (reportType: ReportType) => {
    setOpenExportMenu(false);

    const lcoReportOpts: LCOReportOpts = {
      start,
      end,
      format: 'CSV',
      reportType,
      filters: buildFilters(),
      ...(opts?.orFilters && { orFilters: true }),
    };

    return exportLCOReportingData(lcoReportOpts, {
      appId: appKey,
      companyId,
      returnDownloadUrl: false,
    }).catch((e: Error) => {
      console.error(e.message);
    });
  };

  return {
    showNewPlayReportsUI,
    openExportMenu,
    setOpenExportMenu,
    exportMenuAnchorRef,
    exportData,
  };
};

export const MAX_BULK_EXPORT_PLAYS = 5;

export const buildPlayFilters = (plays: Play[]): ReportFilters =>
  plays.map((play) => [
    { field: 'play_id', operator: 'equals' as const, value: play.id },
    {
      field: 'target_type',
      operator: 'equals' as const,
      value: getTargetType(play),
    },
  ]);

// ---------------------------------------------------------------------------
// V1 (MUI Popover)
// ---------------------------------------------------------------------------

const ExportPlayDataUI: React.FC<{
  isDemoData?: boolean;
  variant?: 'primary' | 'neutral';
  openExportMenu: boolean;
  setOpenExportMenu: (open: boolean) => void;
  exportMenuAnchorRef: React.MutableRefObject<any>;
  showNewPlayReportsUI: boolean | undefined;
  exportData: (reportType: ReportType) => void;
}> = ({
  isDemoData,
  variant = 'primary',
  openExportMenu,
  setOpenExportMenu,
  exportMenuAnchorRef,
  showNewPlayReportsUI,
  exportData,
}) => {
  const isNeutral = variant === 'neutral';
  const textColor = isNeutral ? 'tw-text-neutral-700' : 'tw-text-primary-700';
  const textColorDimmed = isNeutral
    ? 'tw-text-neutral-700/40'
    : 'tw-text-primary-700/40';

  const exportButton = (
    <button
      className={cs(`export-button tw-py-1 tw-font-semibold ${textColor}`, {
        [textColorDimmed]: isDemoData,
      })}
      onClick={() => setOpenExportMenu(true)}
      ref={exportMenuAnchorRef}
      disabled={isDemoData}
    >
      <span className="export-icon tw-px-2">{Icons.export}</span>
      <span>Export CSV</span>
      <span
        className={cs('tw-px-2', {
          'hide-icon': openExportMenu,
        })}
      >
        {Icons.cbrChevronDown}
      </span>
    </button>
  );

  const exportMenu = (
    <Popover
      id="dropdown"
      open={openExportMenu}
      anchorEl={exportMenuAnchorRef?.current}
      onClose={() => setOpenExportMenu(false)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <div className="tw-flex tw-max-w-60 tw-flex-col">
        <button
          onClick={() => {
            exportData(
              showNewPlayReportsUI
                ? ReportType.lifecycle_entity_metrics_customer_snapshot_export
                : ReportType.lifecycle_customer_snapshot
            );
          }}
          className="tw-p-4 tw-text-start hover:tw-bg-primary-50"
        >
          <div className="tw-font-semibold">Customer level report</div>
          <div className="tw-text-xs">
            List of all the customers who engaged with the offers in the play.
          </div>
        </button>
        <button
          onClick={() => {
            exportData(
              showNewPlayReportsUI
                ? ReportType.lifecycle_entity_metrics_user_export
                : ReportType.lifecycle_customer_snapshot_export_user
            );
          }}
          className="tw-p-4 tw-text-start hover:tw-bg-primary-50"
        >
          <div className="tw-font-semibold">User level report</div>
          <div className="tw-text-xs">
            List of all the users who engaged with offers along with their
            status.
          </div>
        </button>
      </div>
    </Popover>
  );

  return (
    <>
      {isDemoData ? (
        <TippyTooltip content={<>Disabled in demo mode</>}>
          <div>{exportButton}</div>
        </TippyTooltip>
      ) : (
        exportButton
      )}
      {exportMenu}
    </>
  );
};

export const ExportPlayData: React.FC<{
  playId: string;
  offerId: string | null | undefined;
  targetType: 'HOSTED_PAGE' | 'OFFER';
}> = ({ playId, offerId, targetType }) => {
  const isDemoData = useShowPlayDemoData();
  const offerSelected = offerId !== null && offerId !== undefined;

  const hook = usePlayExport(() => [
    [
      { field: 'play_id', operator: 'equals', value: playId },
      ...(offerSelected
        ? ([
            { field: 'offer_id', operator: 'equals', value: offerId },
          ] as ReportFilter[])
        : []),
      { field: 'target_type', operator: 'equals', value: targetType },
    ],
  ]);

  return <ExportPlayDataUI isDemoData={isDemoData} {...hook} />;
};

export const BulkExportPlayData: React.FC<{ plays: Play[] }> = ({ plays }) => {
  const isDemoData = useShowPlayDemoData();
  const playsCapped = plays.slice(0, MAX_BULK_EXPORT_PLAYS);
  const hook = usePlayExport(() => buildPlayFilters(playsCapped), {
    orFilters: playsCapped.length > 1,
  });
  return (
    <ExportPlayDataUI isDemoData={isDemoData} variant="neutral" {...hook} />
  );
};

// ---------------------------------------------------------------------------
// V2 (Sting design system)
// ---------------------------------------------------------------------------

const ExportPlayDataUIV2: React.FC<{
  isDemoData?: boolean;
  disabled?: boolean;
  variant?:
    | 'primary'
    | 'neutral'
    | 'neutral-outline'
    | 'primary-ghost'
    | 'primary-outline'
    | 'neutral-ghost'
    | 'danger'
    | 'danger-outline'
    | 'danger-ghost'
    | undefined;
  openExportMenu: boolean;
  setOpenExportMenu: (open: boolean) => void;
  exportData: (reportType: ReportType) => void;
}> = ({
  isDemoData,
  disabled,
  variant = 'primary-ghost',
  openExportMenu,
  setOpenExportMenu,
  exportData,
}) => {
  const exportButton = (
    <SPopover open={openExportMenu} onOpenChange={setOpenExportMenu}>
      <SPopover.Trigger asChild>
        <SButton
          variant={variant}
          icon={<SIcon name="download" size={16} />}
          iconPosition="left"
          disabled={disabled || isDemoData}
        >
          Export CSV
          <SIcon name="chevron-down" size={14} />
        </SButton>
      </SPopover.Trigger>
      <SPopover.Content align="end" className="!tw-overflow-hidden !tw-p-0">
        <div className="tw-flex tw-max-w-80 tw-flex-col">
          <button
            type="button"
            onClick={() =>
              exportData(
                ReportType.lifecycle_entity_metrics_customer_snapshot_export
              )
            }
            className="tw-px-4 tw-py-2 tw-text-start hover:tw-bg-primary-50"
          >
            <div className="tw-font-semibold">Customer level report</div>
            <div className="tw-text-xs">
              List of all the customers who engaged with the offers in the play.
            </div>
          </button>
          <button
            type="button"
            onClick={() =>
              exportData(ReportType.lifecycle_entity_metrics_user_export)
            }
            className="tw-px-4 tw-py-2 tw-text-start hover:tw-bg-primary-50"
          >
            <div className="tw-font-semibold">User level report</div>
            <div className="tw-text-xs">
              List of all the users who engaged with offers along with their
              status.
            </div>
          </button>
        </div>
      </SPopover.Content>
    </SPopover>
  );

  return isDemoData ? (
    <STooltip label="Disabled in demo mode." width="regular" placement="left">
      <div>{exportButton}</div>
    </STooltip>
  ) : (
    exportButton
  );
};

export const ExportPlayDataV2: React.FC<{
  playId: string;
  offerId: string | null | undefined;
  disabled?: boolean;
}> = ({ playId, offerId, disabled }) => {
  const isDemoData = useShowPlayDemoData();
  const offerSelected = offerId !== null && offerId !== undefined;

  const hook = usePlayExport(() => [
    [
      { field: 'play_id', operator: 'equals', value: playId },
      ...(offerSelected
        ? ([
            { field: 'offer_id', operator: 'equals', value: offerId },
          ] as ReportFilter[])
        : []),
      { field: 'target_type', operator: 'equals', value: 'OFFER' },
    ],
  ]);

  return (
    <ExportPlayDataUIV2 isDemoData={isDemoData} disabled={disabled} {...hook} />
  );
};

export type BulkLifecycleExportFilterBuilder = (plays: Play[]) => ReportFilters;

export const BulkExportLifecyclePlayData: React.FC<{
  plays: Play[];
  buildFilters?: BulkLifecycleExportFilterBuilder;
}> = ({ plays, buildFilters = buildPlayFilters }) => {
  const isDemoData = useShowPlayDemoData();
  const playsCapped = plays.slice(0, MAX_BULK_EXPORT_PLAYS);
  const hook = usePlayExport(() => buildFilters(playsCapped), {
    orFilters: playsCapped.length > 1,
  });
  return (
    <ExportPlayDataUIV2
      isDemoData={isDemoData}
      variant="neutral-outline"
      {...hook}
    />
  );
};

const useBulkUnifiedPlayExport = (plays: Play[]) => {
  const appKey = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);
  const companyInternalName = companyId;
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const exportLCOReportingData = useExportCustomersData();
  const dispatch = useAppDispatch();
  const isDemoData = useShowPlayDemoData();
  const { isStarterFreePlan } = usePlans();
  const strings = useStrings();
  const offerPerfNav = useSelector(selectOfferPerfReportNav(appKey));
  const expPerfNav = useSelector(selectExpPerfReportNav(appKey));
  const brandOptions = useGetBrandOptions({ appKey });
  const isNestedAudiencesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_NESTED_AUDIENCES_IN_PERF_REPORTS
  );
  const isMultiBrandFunnelAPIEnabled = useFeatureFlags(
    FeatureFlagsE.SERVER_SIDE_BRAND_FILTER
  );
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const { data: appSettings } =
    companyAppSettingsSlice.endpoints.getAppSettings.useQuery({ appKey });
  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const playsCapped = useMemo(
    () => plays.slice(0, MAX_BULK_EXPORT_PLAYS),
    [plays]
  );
  const isExportSelectionEligible = useMemo(
    () => isBulkExportSelectionEligible(playsCapped),
    [playsCapped]
  );
  const requiresStarterPremium = useMemo(
    () => bulkExportRequiresStarterPremium(playsCapped),
    [playsCapped]
  );
  const brandLabelsById = useMemo(
    () =>
      Object.fromEntries(
        brandOptions.map((brand) => [
          brand.value,
          typeof brand.label === 'string' ? brand.label : String(brand.value),
        ])
      ),
    [brandOptions]
  );
  const starterExportTooltip = strings.UPGRADE_PLAN({
    description: strings.UPGRADE_TO_STARTER_PREMIUM_EXPORT,
  });

  const exportData = useCallback(() => {
    return exportBulkPlaysAsZip(playsCapped, {
      start,
      end,
      appId: appKey,
      companyId,
      isDemoData: Boolean(isDemoData),
      isStarterFreePlan,
      dispatch,
      exportLCOReportingData,
      settings: {
        companyInternalName,
        isNestedAudiencesEnabled,
        isMultiBrandFunnelAPIEnabled,
        mrrActive: Boolean(appSettings?.has_revenue),
        isRepurchaseAttributionEnabled: Boolean(
          billingConfig?.repurchase_based_attribution
        ),
        expPerfNav,
        offerPerfNav,
        brandLabelsById,
      },
    }).catch((error: Error) => {
      console.error(error.message);
    });
  }, [
    appKey,
    appSettings?.has_revenue,
    billingConfig?.repurchase_based_attribution,
    brandLabelsById,
    companyId,
    companyInternalName,
    dispatch,
    end,
    expPerfNav,
    exportLCOReportingData,
    isDemoData,
    isMultiBrandFunnelAPIEnabled,
    isNestedAudiencesEnabled,
    isStarterFreePlan,
    offerPerfNav,
    playsCapped,
    start,
  ]);

  return {
    exportData,
    isDemoData,
    isStarterFreePlan,
    isExportSelectionEligible,
    requiresStarterPremium,
    starterExportTooltip,
    ineligibleSelectionTooltip: BULK_EXPORT_INELIGIBLE_SELECTION_TOOLTIP,
  };
};

export const BulkExportUnifiedPlayData: React.FC<{ plays: Play[] }> = ({
  plays,
}) => {
  const {
    exportData,
    isDemoData,
    isStarterFreePlan,
    isExportSelectionEligible,
    requiresStarterPremium,
    starterExportTooltip,
    ineligibleSelectionTooltip,
  } = useBulkUnifiedPlayExport(plays);
  const isExportDisabled =
    isDemoData ||
    (isStarterFreePlan && requiresStarterPremium) ||
    !isExportSelectionEligible;

  const exportButton = (
    <SButton
      variant="neutral-outline"
      icon={<SIcon name="download" size={16} />}
      iconPosition="left"
      disabled={isExportDisabled}
      onClick={exportData}
    >
      Export CSV
    </SButton>
  );

  if (isDemoData) {
    return (
      <STooltip label="Disabled in demo mode." width="regular" placement="left">
        <div>{exportButton}</div>
      </STooltip>
    );
  }

  if (isStarterFreePlan && requiresStarterPremium) {
    return (
      <TippyTooltip content={starterExportTooltip}>
        <div>{exportButton}</div>
      </TippyTooltip>
    );
  }

  if (!isExportSelectionEligible) {
    return (
      <STooltip
        label={ineligibleSelectionTooltip}
        width="regular"
        placement="left"
      >
        <div>{exportButton}</div>
      </STooltip>
    );
  }

  return exportButton;
};
